import React from "react";
import { BarChart3, PieChart, TrendingUp, Activity } from "lucide-react";

interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'timeline';
  title: string;
  data: Record<string, unknown>[];
  labels?: string[];
  description?: string;
}

interface VisualContent {
  type: 'chart' | 'diagram' | 'infographic' | 'table';
  title: string;
  data: ChartData | any;
  description?: string;
}

interface ChatVisualContentProps {
  content: VisualContent;
}

const normalizeChartValues = (rows: Record<string, unknown>[]): number[] => {
  return rows.map((row, index) => {
    const numericValue = Object.values(row).find((value) => typeof value === 'number');
    if (typeof numericValue === 'number') {
      return numericValue;
    }

    const fallbackValue = Object.values(row)[index] ?? Object.values(row)[0];
    return typeof fallbackValue === 'number' ? fallbackValue : 0;
  });
};

const SimpleBarChart = ({ data, labels, title }: { data: number[], labels?: string[], title: string }) => {
  const maxValue = Math.max(...data);
  
  return (
    <div className="mt-4 p-4 bg-background border rounded-lg">
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <BarChart3 className="h-4 w-4" />
        {title}
      </h4>
      <div className="space-y-2">
        {data.map((value, index) => (
          <div key={index} className="flex items-center gap-3">
            <span className="text-sm w-20 text-right">
              {labels?.[index] || `Item ${index + 1}`}
            </span>
            <div className="flex-1 bg-muted rounded-full h-4 relative overflow-hidden">
              <div
                className="bg-blue-500 h-full rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${(value / maxValue) * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium w-12 text-left">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SimplePieChart = ({ data, labels, title }: { data: number[], labels?: string[], title: string }) => {
  const total = data.reduce((sum, value) => sum + value, 0);
  const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-primary'];
  
  return (
    <div className="mt-4 p-4 bg-background border rounded-lg">
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <PieChart className="h-4 w-4" />
        {title}
      </h4>
      <div className="flex items-center gap-4">
        {/* Simple donut representation */}
        <div className="w-24 h-24 rounded-full border-8 relative flex items-center justify-center bg-muted">
          <span className="text-xs font-medium">Total<br/>{total}</span>
        </div>
        
        {/* Legend */}
        <div className="flex-1 space-y-2">
          {data.map((value, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className={`w-3 h-3 rounded ${colors[index % colors.length]}`} />
              <span className="flex-1">{labels?.[index] || `Category ${index + 1}`}</span>
              <span className="font-medium">
                {value} ({Math.round((value / total) * 100)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SimpleLineChart = ({ data, labels, title }: { data: number[], labels?: string[], title: string }) => {
  const maxValue = Math.max(...data);
  const minValue = Math.min(...data);
  const range = maxValue - minValue;
  
  return (
    <div className="mt-4 p-4 bg-background border rounded-lg">
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4" />
        {title}
      </h4>
      <div className="h-32 relative border rounded bg-muted/20">
        <svg className="w-full h-full p-2" viewBox="0 0 300 100">
          {/* Draw line */}
          <polyline
            points={data.map((value, index) => 
              `${(index / (data.length - 1)) * 280 + 10},${90 - ((value - minValue) / range) * 80}`
            ).join(' ')}
            stroke="rgb(59 130 246)"
            strokeWidth="2"
            fill="none"
            className="animate-pulse"
          />
          
          {/* Draw points */}
          {data.map((value, index) => (
            <circle
              key={index}
              cx={(index / (data.length - 1)) * 280 + 10}
              cy={90 - ((value - minValue) / range) * 80}
              r="3"
              fill="rgb(59 130 246)"
            />
          ))}
        </svg>
        
        {/* Labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-muted-foreground px-2">
          {labels?.slice(0, Math.min(5, labels.length)).map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>
      </div>
      
      <div className="mt-2 text-xs text-muted-foreground">
        Range: {minValue} - {maxValue}
      </div>
    </div>
  );
};

const SimpleTable = ({ data, title }: { data: Record<string, unknown>[], title: string }) => {
  if (!data || data.length === 0) {return null;}
  
  const headers = Object.keys(data[0]);
  
  return (
    <div className="mt-4 p-4 bg-background border rounded-lg overflow-hidden">
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <Activity className="h-4 w-4" />
        {title}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {headers.map((header) => (
                <th key={header} className="text-left p-2 font-medium capitalize">
                  {header.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 10).map((row, index) => (
              <tr key={index} className="border-b last:border-b-0 hover:bg-muted/50">
                {headers.map((header) => (
                  <td key={header} className="p-2">
                    {typeof row[header] === 'number' ? 
                      row[header].toLocaleString() : 
                      String(row[header] || '-')
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {data.length > 10 && (
          <div className="mt-2 text-xs text-muted-foreground text-center">
            Showing first 10 of {data.length} rows
          </div>
        )}
      </div>
    </div>
  );
};

export const ChatVisualContent: React.FC<ChatVisualContentProps> = ({ content }) => {
  const { type, title, data, description } = content;

  const renderContent = () => {
    switch (type) {
      case 'chart': {
        const chartData = data as ChartData;
        const normalizedChartData = {
          ...chartData,
          data: normalizeChartValues(chartData.data),
        };
        switch (chartData.type) {
          case 'bar':
            return <SimpleBarChart {...normalizedChartData} />;
          case 'pie':
            return <SimplePieChart {...normalizedChartData} />;
          case 'line':
            return <SimpleLineChart {...normalizedChartData} />;
          default:
            return <SimpleBarChart {...normalizedChartData} />;
        }
      }

      case 'table':
        return <SimpleTable data={data} title={title} />;
      
      case 'diagram':
      case 'infographic':
        return (
          <div className="mt-4 p-4 bg-background border rounded-lg">
            <h4 className="font-medium mb-2">{title}</h4>
            <p className="text-muted-foreground text-sm">
              {description || "Visual diagram would be displayed here"}
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {renderContent()}
      
      {description && type === 'chart' && (
        <div className="mt-2 text-xs text-muted-foreground">
          💡 {description}
        </div>
      )}
    </div>
  );
};
